import React, { useCallback, useEffect, useState } from "react";
import {
  getPromptFrames,
  updatePromptFrame,
  resetPromptFrame,
  type PromptFrame,
} from "../../services/promptFramesService";
import { findMissingSlots } from "../../utils/promptFrameSlots";

interface PromptFramesTabProps {
  getToken: () => string | undefined;
}

const FRAME_LABELS: Record<PromptFrame["key"], { title: string; blurb: string }> = {
  root: { title: "Root", blurb: "Outer skeleton that arranges the other frames." },
  scenario: { title: "Scenario", blurb: "Wraps the scenario context." },
  persona: { title: "Persona", blurb: "Active persona details and cross-persona awareness." },
  document: { title: "Documents", blurb: "Available documents and retrieved excerpts." },
  rules: { title: "Rules", blurb: "Standing behavioral instructions." },
};

const PromptFramesTab: React.FC<PromptFramesTabProps> = ({ getToken }) => {
  const [frames, setFrames] = useState<PromptFrame[]>([]);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getPromptFrames(getToken());
      setFrames(data);
      setDrafts(Object.fromEntries(data.map((f) => [f.key, f.content])));
    } catch (e: any) {
      setError(e?.message ?? "Failed to load frames");
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    load();
  }, [load]);

  const flashNotice = (message: string) => {
    setNotice(message);
    setTimeout(() => setNotice(null), 3000);
  };

  const handleSave = async (frame: PromptFrame) => {
    const content = drafts[frame.key] ?? "";
    setSavingKey(frame.key);
    setError(null);
    try {
      await updatePromptFrame(frame.key, content, getToken());
      flashNotice(`Saved ${FRAME_LABELS[frame.key].title}`);
      await load();
    } catch (e: any) {
      setError(e?.message ?? "Failed to save");
    } finally {
      setSavingKey(null);
    }
  };

  const handleReset = async (frame: PromptFrame) => {
    setSavingKey(frame.key);
    setError(null);
    try {
      await resetPromptFrame(frame.key, getToken());
      flashNotice(`Reset ${FRAME_LABELS[frame.key].title} to default`);
      await load();
    } catch (e: any) {
      setError(e?.message ?? "Failed to reset");
    } finally {
      setSavingKey(null);
    }
  };

  if (loading) {
    return <p className="text-slate-500 font-body text-sm">Loading frames…</p>;
  }

  return (
    <div className="flex flex-col gap-6">
      {error && (
        <div className="px-4 py-3 rounded-md bg-error/10 text-error font-body text-sm">
          {error}
        </div>
      )}
      {notice && (
        <div className="px-4 py-3 rounded-md bg-primary/10 text-primary font-body text-sm">
          {notice}
        </div>
      )}

      {frames.map((frame) => {
        const label = FRAME_LABELS[frame.key];
        const draft = drafts[frame.key] ?? "";
        const missing = findMissingSlots(frame.key, draft);
        const dirty = draft !== frame.content;
        const busy = savingKey === frame.key;

        return (
          <div
            key={frame.key}
            className="bg-surface-container border border-outline-variant/30 rounded-lg p-5"
          >
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-headline text-lg font-bold text-on-surface">
                {label.title}
              </h3>
              <span
                className={`text-[10px] font-label uppercase tracking-widest px-2 py-0.5 rounded ${
                  frame.source === "override"
                    ? "bg-primary/15 text-primary"
                    : "text-slate-500 border border-outline-variant/30"
                }`}
              >
                {frame.source === "override" ? "Customized" : "Default"}
              </span>
            </div>
            <p className="text-slate-500 font-body text-xs mb-3">{label.blurb}</p>

            {frame.allowedSlots.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {frame.allowedSlots.map((slot) => (
                  <code
                    key={slot}
                    className="text-[11px] font-mono px-1.5 py-0.5 rounded bg-surface-container-high text-slate-400"
                  >
                    {`{{${slot}}}`}
                  </code>
                ))}
              </div>
            )}

            <textarea
              value={draft}
              onChange={(e) =>
                setDrafts((prev) => ({ ...prev, [frame.key]: e.target.value }))
              }
              spellCheck={false}
              rows={Math.min(20, Math.max(6, draft.split("\n").length + 1))}
              className="w-full font-mono text-xs leading-relaxed bg-background border border-outline-variant/30 rounded-md p-3 text-on-surface focus:outline-none focus:border-primary/50"
            />

            {missing.length > 0 && (
              <p className="text-error font-body text-xs mt-2">
                Missing required slot{missing.length > 1 ? "s" : ""}:{" "}
                {missing.map((s) => `{{${s}}}`).join(", ")}
              </p>
            )}

            <div className="flex items-center gap-3 mt-3">
              <button
                onClick={() => handleSave(frame)}
                disabled={!dirty || missing.length > 0 || busy}
                className="px-4 py-2 bg-primary text-on-primary font-bold text-xs rounded-md uppercase tracking-tighter font-label transition-all active:opacity-80 disabled:opacity-40"
              >
                {busy ? "Saving…" : "Save"}
              </button>
              {frame.source === "override" && (
                <button
                  onClick={() => handleReset(frame)}
                  disabled={busy}
                  className="px-4 py-2 text-xs font-label font-bold uppercase tracking-wider text-slate-400 hover:text-on-surface border border-outline-variant/30 rounded-md transition-colors disabled:opacity-40"
                >
                  Reset to default
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default PromptFramesTab;
